/**
 * A screen of a feature above the organization's plan (`tier_not_eligible`)
 * that has demo data to show (#2816, spec 2026-10-26-free-pro-trial.md, «Демо-
 * данные на закрытых экранах»).
 *
 * The rule: below the plan the read route is asked only while the demo is
 * active — then it answers 200 with `demoOnly: true` and demo rows alone — and
 * never otherwise (it would refuse, 403). An answer that is not `demoOnly`, or
 * a 403 `tier_not_eligible` anyway, leaves the closed screen as it was.
 */
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useDemoFilterFromAuth } from '@/features/demo/useDemoFilter';

export interface DemoStatus {
  /** The organization has demo records and no real ones yet. */
  active: boolean;
  /** The demo status is still being read. */
  loading: boolean;
}

/**
 * What a closed feature's screen does:
 * - `off` — the plan is not below; nothing of this applies;
 * - `waiting` — below the plan, the demo status is not known yet (no closed
 *   screen to flash, no request yet);
 * - `closed` — below the plan with no demo: the closed screen, no request;
 * - `peek` — below the plan with the demo active: ask the route.
 */
export type LockedDemoPeek = 'off' | 'waiting' | 'closed' | 'peek';

export function lockedDemoPeek(belowPlan: boolean, demo: DemoStatus): LockedDemoPeek {
  if (!belowPlan) return 'off';
  if (demo.loading) return 'waiting';
  return demo.active ? 'peek' : 'closed';
}

/** The answer says it holds the demo of a closed feature (`demoOnly: true`). */
export function isDemoOnlyAnswer(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { demoOnly?: unknown }).demoOnly === true
  );
}

export interface LockedDemoView {
  /** Closed by the plan: no demo to show, or an answer that is not the demo's — a closed feature's real data is never drawn. */
  closedByPlan: boolean;
  /** Below the plan, drawing the demo (or waiting for it): the feature's actions stay closed. */
  lockedDemo: boolean;
  /** The answer is the demo of a closed feature: the banner goes over it. */
  demoOnly: boolean;
}

/**
 * What a screen of a feature above the plan makes of the gate and of its
 * answer. `answer` is `undefined` (or `null`) until the route has answered.
 *
 * A `demoOnly` answer is believed whatever the client's gate says: the server
 * decides by the plan it sees, so a stale or failed client verdict (`off`,
 * `waiting`) must not draw demo rows as the organization's own, beside enabled
 * paid controls and without the banner.
 */
export function lockedDemoView(peek: LockedDemoPeek, answer: unknown): LockedDemoView {
  const answered = answer !== undefined && answer !== null;
  const demoOnlyAnswer = isDemoOnlyAnswer(answer);
  const closedByPlan =
    peek === 'closed' || (peek === 'peek' && answered && !demoOnlyAnswer);
  return {
    closedByPlan,
    lockedDemo: !closedByPlan && (peek === 'peek' || demoOnlyAnswer),
    demoOnly: demoOnlyAnswer && !closedByPlan,
  };
}

/** The organization's demo status for a screen in either shell (portal or Bitrix24). */
export function useDemoStatus(): DemoStatus {
  const { apiClient, orgId } = useAuth();
  const { includeDemo, isLoading } = useDemoFilterFromAuth(apiClient, orgId);
  return { active: includeDemo === 'yes', loading: isLoading === true };
}
